import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Avatar } from '../lib/avatars';

const MsgIcon = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 1H15V11H9L6 14V11H1V1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
const CloseIcon = () => <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;

export default function ConversationsBar({ session, nearbyUsers, onOpenChat, unreadCounts, setUnreadCounts }) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const myId = session?.user?.id;

  const totalUnread = Object.values(unreadCounts || {}).reduce((a, b) => a + b, 0);

  useEffect(() => {
    if (!myId || !open) return;
    const load = async () => {
      // Load recent messages
      const { data } = await supabase.from('messages').select('*')
        .or('sender_id.eq.' + myId + ',receiver_id.eq.' + myId)
        .order('created_at', { ascending: false });
      if (!data) return;

      // Get unique conversation partners
      const seen = new Set();
      const pairs = [];
      data.forEach(msg => {
        const otherId = msg.sender_id === myId ? msg.receiver_id : msg.sender_id;
        if (!seen.has(otherId)) { seen.add(otherId); pairs.push({ otherId, msg }); }
      });

      if (!pairs.length) return;

      // Fetch profiles for all partners (even if offline)
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, age, gender, avatar_url')
        .in('id', pairs.map(p => p.otherId));

      const convos = pairs.map(({ otherId, msg }) => {
        const nearby = nearbyUsers.find(u => u.id === otherId);
        const profile = (profiles || []).find(p => p.id === otherId);
        const name = nearby?.name || profile?.name || 'Unknown';
        const gender = nearby?.gender || profile?.gender;
        const age = nearby?.age || profile?.age;
        const online = nearby?.online || false;
        const lastMsg = msg.photo_url ? '📷 Photo' : msg.content;
        return {
          id: otherId,
          name, gender, age, online, lastMsg,
          time: msg.created_at,
          isMe: msg.sender_id === myId,
          user: nearby || { id: otherId, name, gender, age, online: false },
        };
      });
      setConversations(convos);
    };
    load();
  }, [myId, open, nearbyUsers]);

  // Realtime new messages
  useEffect(() => {
    if (!myId) return;
    const ch = supabase.channel('convos_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: 'receiver_id=eq.' + myId }, () => {
        if (open) setOpen(o => { if(o) setTimeout(() => setOpen(true), 10); return false; });
      }).subscribe();
    return () => supabase.removeChannel(ch);
  }, [myId, open]);

  return (
    <>
      {/* Bouton flottant — top right sous navbar */}
      <button onClick={() => setOpen(!open)} style={{ position:'fixed', top:'70px', right:'16px', zIndex:50, background:'#0d0d0d', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'50%', width:'44px', height:'44px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 4px 20px rgba(0,0,0,0.6)', color:'white' }}>
        <MsgIcon/>
        {totalUnread > 0 && (
          <div style={{ position:'absolute', top:'-3px', right:'-3px', width:'18px', height:'18px', borderRadius:'50%', background:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:700, color:'#080808', border:'2px solid #080808' }}>
            {totalUnread > 9 ? '9+' : totalUnread}
          </div>
        )}
      </button>

      {/* Panel conversations */}
      {open && (
        <div style={{ position:'fixed', top:'120px', right:'16px', left:'auto', bottom:'auto', zIndex:90, background:'rgba(10,10,10,0.99)', backdropFilter:'blur(20px)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'16px', width:'min(320px,calc(100vw - 32px))', maxHeight:'70vh', display:'flex', flexDirection:'column', boxShadow:'0 8px 40px rgba(0,0,0,0.8)' }}>

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontWeight:700, fontSize:'0.95rem', color:'white' }}>Messages</span>
            <button onClick={() => setOpen(false)} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'50%', width:'28px', height:'28px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'rgba(255,255,255,0.4)' }}><CloseIcon/></button>
          </div>

          <div style={{ overflowY:'auto', flex:1, padding:'0 12px 16px' }}>

            {/* Nearby users — scroll horizontal */}
            {nearbyUsers.filter(u => !conversations.find(c => c.id === u.id)).length > 0 && (
              <div style={{ marginBottom:'18px' }}>
                <div style={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.2)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'10px' }}>Nearby</div>
                <div style={{ display:'flex', gap:'14px', overflowX:'auto', paddingBottom:'4px' }}>
                  {nearbyUsers.filter(u => !conversations.find(c => c.id === u.id)).map(u => (
                    <div key={u.id} onClick={() => { onOpenChat(u); setOpen(false); }} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'5px', cursor:'pointer', flexShrink:0 }}>
                      <div style={{ position:'relative' }}>
                        <Avatar gender={u.gender} userId={u.id} size={46}/>
                        <div style={{ position:'absolute', bottom:'1px', right:'1px', width:'10px', height:'10px', borderRadius:'50%', background: u.online ? '#00d68f' : 'rgba(255,255,255,0.2)', border:'2px solid #0a0a0a' }}/>
                      </div>
                      <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.35)', maxWidth:'46px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textAlign:'center' }}>{u.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conversations */}
            {conversations.length > 0 ? (
              <div>
                <div style={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.2)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'8px' }}>Conversations</div>
                {conversations.map(conv => {
                  const unread = (unreadCounts || {})[conv.id] || 0;
                  return (
                    <div key={conv.id} onClick={() => { onOpenChat(conv.user); if(setUnreadCounts) setUnreadCounts(prev => ({...prev, [conv.id]: 0})); setOpen(false); }}
                      style={{ display:'flex', alignItems:'center', gap:'11px', padding:'10px', borderRadius:'13px', cursor:'pointer', marginBottom:'5px', border:'1px solid rgba(255,255,255,0.04)', background:'rgba(255,255,255,0.02)' }}>
                      <div style={{ position:'relative', flexShrink:0 }}>
                        <Avatar gender={conv.gender} userId={conv.id} size={42}/>
                        {/* Online dot */}
                        <div style={{ position:'absolute', bottom:0, right:0, width:'10px', height:'10px', borderRadius:'50%', background: conv.online ? '#00d68f' : 'rgba(255,255,255,0.2)', border:'2px solid #0a0a0a' }}/>
                        {/* Unread badge */}
                        {unread > 0 && (
                          <div style={{ position:'absolute', top:'-4px', right:'-4px', width:'16px', height:'16px', borderRadius:'50%', background:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8px', fontWeight:700, color:'#080808', border:'2px solid #0a0a0a' }}>
                            {unread}
                          </div>
                        )}
                      </div>
                      <div style={{ flex:1, overflow:'hidden' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'2px' }}>
                          <span style={{ fontWeight: unread>0?700:500, fontSize:'0.88rem', color:'white' }}>
                            {conv.name}{conv.age ? ', ' + conv.age : ''}
                          </span>
                        </div>
                        <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {conv.isMe ? 'You: ' : ''}{conv.lastMsg}
                        </div>
                      </div>
                      <div style={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.18)', flexShrink:0 }}>
                        {new Date(conv.time).toLocaleTimeString('en', { hour:'2-digit', minute:'2-digit' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign:'center', padding:'20px', color:'rgba(255,255,255,0.2)', fontSize:'0.85rem' }}>
                No conversations yet.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
